import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MailWarning, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function useDeliveryStatus(enabled = true) {
  return useQuery({ queryKey: ["email-delivery-status"], queryFn: () => get("/settings/email/delivery-status"), enabled, staleTime: 120_000, refetchInterval: 300_000 });
}

export function RetryFailedButton({ size = "sm" }) {
  const qc = useQueryClient();
  const retry = async () => {
    try { const { data } = await api.post("/settings/email/outbox/retry-failed"); toast.success(`Retrying ${data.retried} email(s)`); qc.invalidateQueries({ queryKey: ["email-delivery-status"] }); qc.invalidateQueries({ queryKey: ["outbox"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <Button size={size} variant="outline" onClick={retry} data-testid="email-retry-failed-btn"><RotateCw size={14} /> Retry failed</Button>;
}

export function EmailAlertBanner() {
  const { user } = useAuth();
  const { data } = useDeliveryStatus(user?.role === "super_admin");
  const n = (data?.failed || 0) + (data?.skipped || 0);
  if (!n) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#EBC8C1] bg-risk-tint px-6 py-2 text-sm text-risk lg:px-10" role="alert" data-testid="email-failure-banner">
      <MailWarning size={16} aria-hidden="true" />
      <span><strong>{n} candidate email{n === 1 ? "" : "s"}</strong> failed or weren't sent in the last 7 days. Latest reason: {data.latest_reason || "unknown"}.</span>
      <Link to="/app/settings/outbox" className="font-medium underline" data-testid="email-banner-outbox-link">Open outbox</Link>
    </div>
  );
}

export function DeliveryStatusCard() {
  const { data } = useDeliveryStatus();
  if (!data) return null;
  const n = data.failed + data.skipped;
  return (
    <section className={`panel flex flex-wrap items-center gap-4 p-5 ${n ? "border-[#EBC8C1]" : ""}`} data-testid="email-delivery-card">
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold">Email delivery, last 7 days</h3>
        <p className="text-sm text-ink-secondary" data-testid="email-delivery-counts">{data.sent} sent · {data.failed} failed · {data.skipped} not sent</p>
        {n > 0 && <p className="text-xs text-risk" data-testid="email-delivery-reason">Latest problem: {data.latest_reason}</p>}
      </div>
      {n > 0 && <RetryFailedButton />}
    </section>
  );
}
